/*
 * Saved Templates tab — extracts a row's colour swatches, either straight
 * from the manifest's `themeColors` or, failing that, by downloading +
 * parsing the source .pptx (like useSlideTemplateThumbnail.ts does for real
 * photos). A module-level cache (keyed by sourceFileUrl) keeps repeat
 * renders/re-selections free.
 */
import { useEffect, useRef, useState } from "react";
import { extractBrand } from "../services/brandExtractor";
import { fetchSavedBrandTemplateBytes, logSavedBrandTemplatesError, type SavedBrandTemplate } from "../services/brandTemplatesApi";
import { normalizeHex } from "../../../utils/colourUtils";
import type { BrandSpec } from "../../../types";

const swatchCache = new Map<string, Promise<readonly string[]>>();

function brandSwatches(brand: BrandSpec): string[] {
  return [
    brand.colors.accent1, brand.colors.accent2, brand.colors.accent3,
    brand.colors.accent4, brand.colors.accent5, brand.colors.accent6,
  ].map((c) => normalizeHex(c)).filter((c): c is string => c !== null);
}

function loadSwatches(cacheKey: string, candidateUrls: readonly string[]): Promise<readonly string[]> {
  let cached = swatchCache.get(cacheKey);
  if (!cached) {
    cached = fetchSavedBrandTemplateBytes(candidateUrls)
      .then((buffer) => extractBrand(buffer))
      .then((brand) => brandSwatches(brand));
    cached.catch(() => swatchCache.delete(cacheKey));
    swatchCache.set(cacheKey, cached);
  }
  return cached;
}

/** Empty until swatches are available or extraction fails — no separate
 *  error UI, the row just shows no swatches. Prefers `colors` directly;
 *  falls back to downloading + extracting the .pptx when there is one. */
export function useSavedBrandTemplateSwatches(template: SavedBrandTemplate): readonly string[] {
  const { colors, sourceFileUrl, sourceFileUrlCandidates } = template;
  const [swatches, setSwatches] = useState<readonly string[]>(colors ?? []);
  const startedRef = useRef<string | null>(null);

  useEffect(() => {
    if (colors) {
      setSwatches(colors);
      return;
    }
    if (!sourceFileUrl) {
      setSwatches([]);
      return;
    }
    if (startedRef.current === sourceFileUrl) return;
    startedRef.current = sourceFileUrl;
    let cancelled = false;
    setSwatches([]);
    loadSwatches(sourceFileUrl, sourceFileUrlCandidates)
      .then((result) => {
        if (!cancelled) setSwatches(result);
      })
      .catch((error: unknown) => {
        logSavedBrandTemplatesError(
          "saved-brand-templates-swatch-extract",
          error instanceof Error ? error.message : "Swatch extraction failed",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [colors, sourceFileUrl, sourceFileUrlCandidates]);

  return swatches;
}
